import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { aiApi } from '../service/ai';
import {
    SuggestionProps,
    SuggestionsState,
} from '../service/suggestions.interface';
import { RootState } from '../../../../../store';

const initialState: SuggestionsState = {
    tooltips: [],
    status: {
        isLoading: false,
        isSuccess: false,
    },
};

export const suggestionsSlice = createSlice({
    name: 'suggestions',
    initialState,
    reducers: {
        setTooltips(state, action: PayloadAction<SuggestionProps[]>) {
            state.tooltips = action.payload;
        },
        addTooltip(state) {
            if (state.tooltips.length < 6) {
                state.tooltips.push({ title: '', text: '' });
            }
        },
        updateTooltip(
            state,
            action: PayloadAction<{
                index: number;
                field: keyof SuggestionProps;
                value: string;
            }>
        ) {
            const { index, field, value } = action.payload;
            if (state.tooltips[index]) {
                state.tooltips[index][field] = value;
            }
        },
        removeTooltip(state, action: PayloadAction<number>) {
            state.tooltips = state.tooltips.filter(
                (_, index) => index !== action.payload
            );
        },
    },
    extraReducers: builder => {
        builder
            .addMatcher(aiApi.endpoints.readSuggestions.matchPending, state => {
                state.status.isLoading = true;
                state.status.isSuccess = false;
            })
            .addMatcher(
                aiApi.endpoints.readSuggestions.matchFulfilled,
                (state, { payload }) => {
                    state.tooltips = [...state.tooltips, ...payload];
                    state.status.isLoading = false;
                    state.status.isSuccess = true;
                }
            )
            .addMatcher(
                aiApi.endpoints.readSuggestions.matchRejected,
                state => {
                    state.status.isLoading = false;
                    state.status.isSuccess = false;
                }
            )
            .addMatcher(
                aiApi.endpoints.createSuggestions.matchFulfilled,
                (state, { payload }) => {
                    state.tooltips = [...state.tooltips, ...payload];
                    state.status = {
                        ...state.status,
                        isLoading: false,
                        isSuccess: true,
                    };
                }
            )
            .addMatcher(
                aiApi.endpoints.createSuggestions.matchPending,
                state => {
                    state.status.isLoading = true;
                    state.status.isSuccess = false;
                }
            );
    },
});

export const selectSuggestionsState = (state: RootState): SuggestionsState =>
    state.suggestions;

export const { setTooltips, addTooltip, updateTooltip, removeTooltip } =
    suggestionsSlice.actions;
export const suggestionsReducer = suggestionsSlice.reducer;
